import {
    addDays,
    addYears,
    formatDistance,
    getYear,
    isBefore,
    isSameDay,
    setYear,
    startOfDay,
    startOfToday,
} from '@core/date.facade';
import { toDateStringDE } from '@core/functions/to-date-string';
import { TolgeeService } from '@core/translation/tolgee.service';

export interface PersonBirthdayHint {
    nextBirthDate: Date;
    nextBirthDateString: string;
    nextBirthDateDistance: string;
}

export function resolvePersonBirthdayHint(
    birthDate: Date,
    referenceDate: Date = startOfToday(),
): PersonBirthdayHint | null {
    if (!birthDate) {
        return null;
    }

    const reference = startOfDay(referenceDate);
    const birthdayThisYear = startOfDay(setYear(birthDate, getYear(reference)));
    const nextBirthDate = isBefore(birthdayThisYear, reference)
        ? addYears(birthdayThisYear, 1)
        : birthdayThisYear;

    return {
        nextBirthDate,
        nextBirthDateString: toDateStringDE(nextBirthDate),
        nextBirthDateDistance: formatBirthdayDistance(nextBirthDate, reference),
    };
}

function formatBirthdayDistance(nextBirthDate: Date, referenceDate: Date): string {
    const reference = startOfDay(referenceDate);
    const target = startOfDay(nextBirthDate);

    if (isSameDay(target, reference)) {
        return TolgeeService.translate('person.core.birthDateHint.today', {}, 'heute');
    }

    if (isSameDay(target, addDays(reference, 1))) {
        return TolgeeService.translate('person.core.birthDateHint.tomorrow', {}, 'morgen');
    }

    if (isSameDay(target, addDays(reference, 2))) {
        return TolgeeService.translate('person.core.birthDateHint.dayAfterTomorrow', {}, 'übermorgen');
    }

    const distance = toDativeDistance(formatDistance(reference, target));
    return TolgeeService.translate('person.core.birthDateHint.inDistance', {distance}, `in ${distance}`);
}

/**
 * date-fns emits the nominative ("7 Monate"), but "in …" governs the dative ("in 7 Monaten").
 * German-only fixup — other locales inflect differently and must be left untouched.
 */
function toDativeDistance(distance: string): string {
    if (!TolgeeService.getLanguage().startsWith('de')) {
        return distance;
    }
    return distance.endsWith('e') ? `${distance}n` : distance;
}
